import React, { useState } from "react";
import axios from "axios";
import "./BeautyPostCard.css";

const BeautyPostCard = ({ post, onUpdate, onDelete }) => {
  const [liked, setLiked] = useState(false);
  const [saved, setSaved] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [newComment, setNewComment] = useState("");
  const [loading, setLoading] = useState(false);

  const handleLike = async () => {
    try {
      const endpoint = liked
        ? `/beauty/post/${post._id}/like`
        : `/beauty/post/${post._id}/like`;

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}${endpoint}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`
          }
        }
      );

      setLiked(!liked);
      onUpdate(response.data.data);
    } catch (error) {
      console.error("Erro ao curtir:", error);
    }
  };

  const handleSave = async () => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/beauty/post/${post._id}/save`,
        {},
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`
          }
        }
      );

      setSaved(!saved);
      onUpdate(response.data.data);
    } catch (error) {
      console.error("Erro ao salvar:", error);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;

    try {
      setLoading(true);
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/beauty/post/${post._id}/comment`,
        { content: newComment },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`
          }
        }
      );

      setNewComment("");
      onUpdate(response.data.data);
    } catch (error) {
      console.error("Erro ao comentar:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleShare = async () => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/beauty/post/${post._id}/share`,
        {},
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`
          }
        }
      );

      onUpdate(response.data.data);
    } catch (error) {
      console.error("Erro ao compartilhar:", error);
    }
  };

  const handleDelete = async () => {
    if (window.confirm("Tem certeza que deseja deletar este post?")) {
      try {
        await axios.delete(
          `${process.env.REACT_APP_API_URL}/beauty/post/${post._id}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`
            }
          }
        );

        onDelete(post._id);
      } catch (error) {
        console.error("Erro ao deletar:", error);
      }
    }
  };

  const postTypeLabel = {
    makeup_tutorial: "💄 Tutorial de Maquiagem",
    skincare_routine: "🧴 Rotina de Skincare",
    product_review: "⭐ Avaliação de Produto",
    before_after: "✨ Antes e Depois",
    hair_styling: "💇 Styling de Cabelo",
    nail_art: "💅 Nail Art",
    makeup_look: "💋 Look de Maquiagem",
    skincare_tips: "💡 Dicas de Skincare",
    inspiration: "🌟 Inspiração",
    product_haul: "🛍️ Comprinhas",
    recommendation: "🎯 Recomendação"
  };

  return (
    <div className="beauty-post-card">
      {/* Header do Post */}
      <div className="post-header">
        <div className="author-info">
          <img
            src={post.author.avatar || post.author.profilePicture}
            alt={post.author.name}
            className="author-avatar"
          />
          <div>
            <h4>{post.author.name}</h4>
            <p className="post-type">
              {postTypeLabel[post.postType] || post.postType}
            </p>
            <span className="post-date">
              {new Date(post.createdAt).toLocaleDateString("pt-BR")}
            </span>
          </div>
        </div>
        <div className="post-menu">
          <button className="menu-btn">⋯</button>
        </div>
      </div>

      {/* Título e Descrição */}
      <div className="post-content">
        <h3>{post.title}</h3>
        <p className="description">{post.description}</p>

        {post.difficulty && (
          <div className="difficulty-badge">
            Nível: <span>{post.difficulty}</span>
          </div>
        )}

        {post.duration > 0 && (
          <div className="duration-badge">
            ⏱️ {post.duration} minutos
          </div>
        )}
      </div>

      {/* Imagens */}
      {post.images && post.images.length > 0 && (
        <div className="post-images">
          {post.images.length === 1 ? (
            <img src={post.images[0].url} alt="post" />
          ) : (
            <div className="images-grid">
              {post.images.slice(0, 4).map((image, index) => (
                <div key={index} className="image-item">
                  <img src={image.url} alt={`post-${index}`} />
                  {index === 3 && post.images.length > 4 && (
                    <div className="more-indicator">
                      +{post.images.length - 4}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Vídeos */}
      {post.videos && post.videos.length > 0 && (
        <div className="post-videos">
          {post.videos.slice(0, 2).map((video, index) => (
            <div key={index} className="video-container">
              <video controls width="100%">
                <source src={video.url} type="video/mp4" />
              </video>
            </div>
          ))}
        </div>
      )}

      {/* Tags e Hashtags */}
      <div className="post-tags">
        {post.tags?.skinTypes && post.tags.skinTypes.length > 0 && (
          <div className="tag-group">
            {post.tags.skinTypes.map((type) => (
              <span key={type} className="tag skin-type-tag">
                {type}
              </span>
            ))}
          </div>
        )}
        {post.hashtags && post.hashtags.length > 0 && (
          <div className="tag-group">
            {post.hashtags.slice(0, 5).map((tag) => (
              <span key={tag} className="tag hashtag-tag">
                {tag}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Produtos Mencionados */}
      {post.products && post.products.length > 0 && (
        <div className="products-section">
          <h4>Produtos Mencionados</h4>
          <div className="products-list">
            {post.products.slice(0, 3).map((product, index) => (
              <div key={index} className="product-item">
                <p>
                  <strong>{product.name}</strong> por {product.brand}
                </p>
                {product.rating > 0 && (
                  <div className="rating">
                    ⭐ {product.rating}/5
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Estatísticas */}
      <div className="post-stats">
        <span>❤️ {post.stats?.likeCount || 0} curtidas</span>
        <span>💬 {post.stats?.commentCount || 0} comentários</span>
        <span>👁️ {post.stats?.viewCount || 0} visualizações</span>
      </div>

      {/* Botões de Ação */}
      <div className="post-actions">
        <button
          className={`action-btn ${liked ? "active" : ""}`}
          onClick={handleLike}
        >
          ❤️ Curtir
        </button>
        <button
          className="action-btn"
          onClick={() => setShowComments(!showComments)}
        >
          💬 Comentar
        </button>
        <button className="action-btn" onClick={handleShare}>
          🔗 Compartilhar
        </button>
        <button
          className={`action-btn ${saved ? "active" : ""}`}
          onClick={handleSave}
        >
          📌 Salvar
        </button>
      </div>

      {/* Seção de Comentários */}
      {showComments && (
        <div className="comments-section">
          <div className="add-comment">
            <input
              type="text"
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder="Adicione um comentário..."
              onKeyPress={(e) => {
                if (e.key === "Enter") {
                  handleAddComment();
                }
              }}
            />
            <button
              onClick={handleAddComment}
              disabled={loading || !newComment.trim()}
            >
              Enviar
            </button>
          </div>

          <div className="comments-list">
            {post.comments && post.comments.slice(0, 5).map((comment) => (
              <div key={comment._id} className="comment">
                <img
                  src={comment.user.avatar}
                  alt={comment.user.name}
                  className="comment-avatar"
                />
                <div className="comment-content">
                  <p>
                    <strong>{comment.user.name}</strong> {comment.content}
                  </p>
                  <span className="comment-date">
                    {new Date(comment.createdAt).toLocaleDateString("pt-BR")}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default BeautyPostCard;
